import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Apple, ArrowDown, ArrowUpRight } from "lucide-react";
import { EASE } from "@/lib/motion";
import { goCustomer } from "@/lib/links";
import { useBusinessCta } from "@/lib/useBusinessCta";

const HERO_IMG =
  "https://images.unsplash.com/photo-1650468770060-bce89d718bdf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBoYWlyJTIwc2Fsb24lMjBkYXJrJTIwYWVzdGhldGljfGVufDB8fHx8MTc4NTAzOTk2NHww&ixlib=rb-4.1.0&q=85";

const Line = ({ children, delay = 0 }) => (
  // No overflow-clip: italic Playfair swashes (A cap, y tail) need to render
  // outside the letter's advance box. Reveal is done via opacity + small y
  // rise so no mask is required.
  <motion.span
    className="block pb-[0.32em]"
    initial={{ opacity: 0, y: "35%" }}
    animate={{ opacity: 1, y: "0%" }}
    transition={{ duration: 1.1, ease: EASE, delay }}
  >
    {children}
  </motion.span>
);

export default function Hero() {
  const ref = useRef(null);
  const { ready: businessCtaReady, onClick: handleBusinessClick } = useBusinessCta();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.16]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-[#F7F5F2]"
    >
      {/* Subtle gold accent glow */}
      <div className="pointer-events-none absolute -right-40 -top-20 h-[620px] w-[620px] rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.12),transparent_70%)]" />

      <div className="relative z-10 mx-auto grid min-h-screen max-w-[1600px] grid-cols-1 items-center gap-12 px-6 pb-16 pt-32 md:px-12 lg:grid-cols-12 lg:gap-10">
        {/* Text column */}
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 1 }}
            className="overline mb-6"
          >
            Last-minute beauty & grooming, on demand
          </motion.p>

          <h1 className="font-serif font-light leading-[0.84] tracking-tighter text-[#2A2724] text-[20vw] sm:text-[15vw] lg:text-[11vw]">
            <Line delay={0.35}>Book</Line>
            <Line delay={0.45}>
              <span className="italic text-gold">Away</span>
            </Line>
          </h1>

          <div className="mt-8 max-w-xl overflow-hidden">
            <motion.p
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 1, ease: EASE, delay: 0.75 }}
              className="font-serif text-3xl md:text-4xl lg:text-5xl italic text-[#2A2724]/80 leading-tight"
            >
              Get Booked In, last minute.
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8, ease: EASE }}
            className="mt-10 grid max-w-2xl grid-cols-1 gap-4 sm:grid-cols-2"
          >
            {/* For Customers */}
            <button
              data-testid="hero-download-btn"
              onClick={() => goCustomer()}
              className="group flex flex-col items-start rounded-2xl bg-gold p-5 text-left text-black transition-colors duration-300 hover:bg-[#AA8627]"
            >
              <p className="mb-2 text-[0.72rem] font-medium uppercase tracking-[0.28em] text-black/70">
                For Customers
              </p>
              <h3 className="font-serif text-xl md:text-2xl font-semibold leading-snug text-black">
                Book your look,{" "}
                <span className="italic">last minute.</span>
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-black/70">
                Openings near you, on demand.
              </p>
              <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-black">
                <Apple className="h-4 w-4" />
                Download App
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </button>

            {/* For Businesses */}
            <button
              data-testid="hero-business-btn"
              onClick={handleBusinessClick}
              disabled={!businessCtaReady}
              aria-busy={!businessCtaReady}
              className="group flex flex-col items-start rounded-2xl border border-black/10 bg-[#F7F5F2] p-5 text-left transition-colors duration-300 hover:border-gold disabled:cursor-progress disabled:opacity-80"
            >
              <p className="overline mb-2">For Businesses</p>
              <h3 className="font-serif text-xl md:text-2xl font-semibold leading-snug text-[#2A2724]">
                Turn empty slots into{" "}
                <span className="italic text-gold">revenue.</span>
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-[#6B6B6B]">
                Fill last-minute cancellations, instantly.
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#2A2724] transition-colors duration-300 group-hover:text-gold">
                I'm a Business
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </button>
          </motion.div>
        </motion.div>

        {/* Image column */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, ease: EASE, delay: 0.5 }}
          className="relative lg:col-span-5"
        >
          <div className="relative aspect-[3/4] w-full overflow-hidden rounded-[2rem] border border-gold/40">
            <motion.img
              style={{ y: imgY, scale: imgScale }}
              src={HERO_IMG}
              alt="Luxury salon interior"
              className="absolute inset-0 h-[118%] w-full object-cover object-center"
            />
          </div>
          {/* Floating stat card */}
          <div className="absolute -bottom-6 -left-6 hidden rounded-2xl border border-black/10 bg-[#F7F5F2]/90 p-5 shadow-[0_24px_70px_-24px_rgba(0,0,0,0.3)] backdrop-blur-xl md:block">
            <p className="font-serif text-4xl text-[#2A2724]">
              4<span className="text-gold">min</span>
            </p>
            <p className="mt-1 max-w-[9rem] text-xs leading-relaxed text-[#6B6B6B]">
              median time to fill a cancelled slot
            </p>
          </div>
        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-[#9a9a9a]"
      >
        <ArrowDown className="h-5 w-5 animate-bounce" />
      </motion.div>
    </section>
  );
}
